/**
 * HesaAdmissionsSnapshot — Section 3 fallback for HESA-verified UK courses.
 *
 * When the editorial `admissions` block is absent, HESA still tells us
 * plenty of useful shape:
 *   * `tariff_sample_size` / `entry_qualifications.sample_size` /
 *     `nss_sample_size` / `employment_sample_size` — how many students
 *     make up each cohort. These proxy admissions volume.
 *   * `continuation_rate` — proportion of the intake that stayed past
 *     Year 1 (proxies fit / rigor).
 *   * `ucas_pathways` — the UCAS application codes / campuses.
 *   * `tef.overall` — the OfS Teaching Excellence Framework rating.
 *
 * We render all of the above as a compact card, above the existing
 * "Not published — university-level acceptance rate" fallback line.
 */
import { Card } from "@/components/ui/card";
import { Ev } from "@/components/app/degree/DegreeDetailBits";
import { Users, TrendingUp, Award, ExternalLink } from "lucide-react";

/** Inline small-sample warning. HESA suppresses at ~22.5 → we treat
 * anything under 30 as "indicative only" for user-facing signals. */
function SmallSample({ n, threshold = 30, label = "small sample" }) {
  if (n == null || n >= threshold) return null;
  return (
    <span
      className="ml-1 inline-flex items-center gap-0.5 rounded-sm border border-amber-300 bg-amber-50 px-1 py-[1px] text-[9px] font-underground-sc tracking-wider text-amber-800"
      title={`Sample size ${n} · HESA suppresses below ~22.5 · treat as indicative only`}
      data-testid="small-sample-badge"
    >
      ⚠ {label} n={n}
    </span>
  );
}
export { SmallSample };

const TEF_COLOR = {
  Gold:                    "bg-amber-100 text-amber-900 border-amber-300",
  Silver:                  "bg-stone-100 text-stone-800 border-stone-300",
  Bronze:                  "bg-orange-100 text-orange-900 border-orange-300",
  "Requires improvement":  "bg-red-100 text-red-900 border-red-300",
};

export function HesaAdmissionsSnapshot({ hesa, uniName, courseTitle, advertisedTariff }) {
  if (!hesa) return null;
  const tariff_n     = hesa.tariff_sample_size;
  const qual_n       = hesa.entry_qualifications?.sample_size;
  const nss_n        = hesa.nss_sample_size;
  const empl_n       = hesa.employment_sample_size;
  const cont_n       = hesa.continuation_sample_size;
  const contRate     = hesa.continuation_rate;
  const pathways     = hesa.ucas_pathways || [];
  const tef          = hesa.tef || {};
  const entryProfile = hesa.entry_profile || null;
  const ucasEoc      = hesa.ucas_eoc || null;
  const eocReady     = ucasEoc && ucasEoc._status === "INGESTED";

  const cohortEst = Math.max(tariff_n || 0, qual_n || 0, nss_n || 0, cont_n || 0);
  if (!cohortEst && !pathways.length && !tef.overall) return null;

  return (
    <Card className="p-4 border-stone-200 bg-white mb-3" data-testid="hesa-admissions-snapshot">
      <div className="label-uppercase !text-[10px] text-stone-500 mb-3">
        HESA Discover Uni · what the numbers tell us
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {cohortEst > 0 && (
          <div data-testid="admissions-cohort">
            <div className="flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider text-stone-500">
              <Users className="h-3 w-3" /> Recent intake
            </div>
            <div className="font-chip text-2xl font-semibold text-stone-900 mt-1">
              ~{cohortEst}<span className="text-sm font-normal text-stone-500 ml-1">students</span>
            </div>
            <div className="text-[10px] text-stone-500 mt-0.5">
              (peak cohort across HESA datasets)
            </div>
          </div>
        )}
        {contRate != null && (
          <div data-testid="admissions-continuation">
            <div className="flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider text-stone-500">
              <TrendingUp className="h-3 w-3" /> Continuation rate
            </div>
            <div className="font-chip text-2xl font-semibold text-emerald-800 mt-1">
              {Math.round(contRate * 100)}%
            </div>
            <div className="text-[10px] text-stone-500 mt-0.5">
              stay past Year 1 (n = {cont_n})
              <SmallSample n={cont_n} />
            </div>
          </div>
        )}
        {tef.overall && (
          <div data-testid="admissions-tef">
            <div className="flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider text-stone-500">
              <Award className="h-3 w-3" /> TEF (OfS)
            </div>
            <div className="mt-1">
              <span
                data-testid="admissions-tef-badge"
                className={`inline-flex items-center px-3 py-1 rounded-full border font-mono text-sm font-semibold ${
                  TEF_COLOR[tef.overall] || "bg-stone-100 text-stone-800 border-stone-300"
                }`}
              >
                {tef.overall}
              </span>
            </div>
            {(tef.student_experience || tef.student_outcomes) && (
              <div className="text-[10px] text-stone-500 mt-1">
                Experience {tef.student_experience || "—"} · Outcomes {tef.student_outcomes || "—"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detailed n-per-dataset row */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-stone-600">
        {tariff_n ? <div>Tariff cohort: <b>{tariff_n}</b><SmallSample n={tariff_n} /></div> : null}
        {qual_n ? <div>Entry-quals cohort: <b>{qual_n}</b><SmallSample n={qual_n} /></div> : null}
        {nss_n ? <div>NSS respondents: <b>{nss_n}</b><SmallSample n={nss_n} /></div> : null}
        {empl_n ? <div>Graduate outcomes: <b>{empl_n}</b><SmallSample n={empl_n} /></div> : null}
      </div>

      {/* UCAS pathways — HESA's ucas_id is the KIS internal programme
          id, not the applicant-portal course code. Render each as a
          reference badge and give one working "Search on UCAS" CTA. */}
      {pathways.length > 0 && (
        <div className="mt-4" data-testid="admissions-ucas-pathways">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
            <div className="label-uppercase !text-[10px] text-stone-500">
              UCAS pathway{pathways.length > 1 ? "s" : ""}
            </div>
            <a
              href={
                "https://digital.ucas.com/search?query=" +
                encodeURIComponent([uniName, courseTitle].filter(Boolean).join(" ").trim())
              }
              target="_blank"
              rel="noreferrer"
              data-testid="admissions-ucas-search-cta"
              className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
            >
              Search on UCAS
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {pathways.map((p, i) => (
              <span
                key={i}
                data-testid={`ucas-pathway-${i}`}
                title="HESA KIS programme identifier — use the search link above to open UCAS."
                className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-3 py-1 text-[11px] text-stone-700"
              >
                <span className="font-underground-sc tracking-wider text-[9px] opacity-60">KIS</span>
                <span className="font-mono">{p.ucas_id}</span>
                {p.location_id && <span className="text-stone-500">· {p.location_id}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Who actually got in — descriptive HESA intake summary (NOT the offer bar) */}
      {entryProfile?.tariff && (
        <div className="mt-4 rounded-md border border-[#4C1D95]/20 bg-[#4C1D95]/5 p-3" data-testid="admissions-selectivity">
          <div className="label-uppercase !text-[10px] text-[#4C1D95] mb-0.5">
            Who actually got in?
          </div>
          <div className="text-[10px] text-stone-500 mb-2">
            Descriptive — what students who <em>arrived</em> had at entry. Not the offer bar.
          </div>
          {advertisedTariff?.standard != null && (
            <div className="mb-2 text-[11px] rounded-sm bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1.5"
              data-testid="admissions-advertised-vs-actual">
              <b>Advertised standard offer:</b> {advertisedTariff.standard} pts
              {advertisedTariff.contextual != null && <> · <b>contextual:</b> {advertisedTariff.contextual} pts</>}
              {" · "}
              <b>median arrival:</b> {entryProfile.tariff.median_tariff} pts
              {(advertisedTariff.standard - entryProfile.tariff.median_tariff) >= 24 && (
                <span className="ml-1 text-amber-700">
                  (Δ likely reflects Clearing + contextual admits)
                </span>
              )}
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Median admit</div>
              <div className="font-mono text-lg font-semibold text-stone-900">
                {entryProfile.tariff.median_tariff}<span className="text-xs text-stone-500 ml-1">UCAS pts</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Top 25% cutoff</div>
              <div className="font-mono text-lg font-semibold text-stone-900">
                {entryProfile.tariff.top_quartile_tariff}<span className="text-xs text-stone-500 ml-1">UCAS pts</span>
              </div>
            </div>
            {entryProfile.qualifications && (
              <div>
                <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Dominant route</div>
                <div className="text-sm font-semibold text-stone-900 capitalize">
                  {entryProfile.qualifications.dominant_route.replace(/_/g, " ")}
                  <span className="ml-1 text-stone-500 font-normal">({entryProfile.qualifications.dominant_pct.toFixed(0)}% of admits)</span>
                </div>
              </div>
            )}
          </div>
          <div className="mt-2 text-[10px] text-stone-500">
            Sample size {entryProfile.tariff.sample_size} admits · Discover Uni KIS 2025 (OGL v3.0)
            {entryProfile.tariff.sample_size < 30 && (
              <span className="ml-1 text-amber-700 font-medium" data-testid="admissions-small-sample-warning">
                · ⚠ small sample — treat as indicative only
              </span>
            )}
          </div>
        </div>
      )}

      {/* UCAS End-of-Cycle — offer rate slot. Renders real values when
          the annual UCAS EoC ingester has populated data; otherwise a
          "coming soon" note explaining the schema is live. */}
      {ucasEoc && eocReady && (
        <div className="mt-3 rounded-md border border-emerald-300 bg-emerald-50/60 p-3" data-testid="admissions-ucas-eoc">
          <div className="label-uppercase !text-[10px] text-emerald-800 mb-1.5">
            UCAS end-of-cycle · offer rate ({ucasEoc.evidence?.academic_year})
          </div>
          <div className="grid md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Applications</div>
              <div className="font-mono text-lg font-semibold text-stone-900">{(ucasEoc.applications || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Offers</div>
              <div className="font-mono text-lg font-semibold text-stone-900">{(ucasEoc.offers || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Offer rate</div>
              <div className="font-mono text-lg font-semibold text-emerald-800">
                {ucasEoc.offer_rate != null ? `${(ucasEoc.offer_rate * 100).toFixed(0)}%` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Accepted</div>
              <div className="font-mono text-lg font-semibold text-stone-900">{(ucasEoc.accepted || 0).toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}
      {ucasEoc && !eocReady && (
        <div className="mt-3 rounded-md border border-stone-300 bg-stone-50 p-3 text-xs text-stone-600" data-testid="admissions-ucas-eoc-pending">
          <b>Course-level offer counts not published.</b>{" "}
          UCAS's free End-of-Cycle bundle only publishes applications &amp; acceptances at the <em>provider</em>{" "}
          level (mirrored into the "Acceptance rate" chip on this course's uni page as{" "}
          <code className="text-[10px]">ucas-eoc-acceptance-rate</code>). Per-course{" "}
          applications-to-offers ratios require a paid UCAS Data &amp; Analysis Custom Data request and{" "}
          aren&#39;t on our public roadmap. The <code className="text-[10px]">hesa_stats.ucas_eoc</code>{" "}
          schema stays live so course-level numbers slot in without a code change if we ever ingest them.
          Selectivity signal above uses Discover Uni&#39;s admitted-student profile.
        </div>
      )}

      <Ev ev={{
        source:       "HESA Discover Uni 2025/26 · CC-BY-4.0",
        confidence:   "verified",
        url:          "https://discoveruni.gov.uk/",
      }} />
    </Card>
  );
}

export default HesaAdmissionsSnapshot;
